import { useState } from 'react';
import { Box, Typography, ToggleButtonGroup, ToggleButton } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { ConfigureScriptStepProps } from './types';
import { JsonSchemaFormEditor, RawJsonEditor } from '@src/components';
import { colors } from '@src/styles';
import { monoFontFamily, noticeSX } from '../../scriptsStyles';

export const ConfigureScriptStep = ({ schema, formData, onFormDataChange }: ConfigureScriptStepProps) => {
  const [viewMode, setViewMode] = useState<'schema' | 'raw'>('schema');
  const [jsonValue, setJsonValue] = useState<string>(JSON.stringify(formData || {}, null, 2));
  const [jsonErrors, setJsonErrors] = useState<string[]>([]);

  if (!schema) {
    return (
      <Box sx={noticeSX(colors.status.pending)}>
        <WarningAmberRoundedIcon />
        Schema não disponível para este template.
      </Box>
    );
  }

  const handleViewModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: 'schema' | 'raw' | null) => {
    if (newMode !== null) {
      setViewMode(newMode);
      if (newMode === 'raw') {
        setJsonValue(JSON.stringify(formData || {}, null, 2));
      }
    }
  };

  const handleFormChange = (newDataJson: string) => {
    try {
      const parsed = JSON.parse(newDataJson);
      onFormDataChange(parsed);
      setJsonValue(JSON.stringify(parsed, null, 2));
      setJsonErrors([]);
    } catch (error) {
      setJsonErrors(['Erro ao processar dados do formulário']);
    }
  };

  const handleJsonChange = (newJson: string) => {
    setJsonValue(newJson);
    try {
      const parsed = JSON.parse(newJson);
      onFormDataChange(parsed);
      setJsonErrors([]);
    } catch (error) {
      setJsonErrors(['JSON inválido']);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 1.5,
        }}
      >
        <Typography sx={{ color: colors.brand.textSecondary, fontSize: '13.5px' }}>
          Configure os parâmetros do script canônico.
        </Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          size="small"
          sx={{
            padding: '3px',
            gap: '2px',
            borderRadius: '8px',
            border: `1px solid ${colors.brand.line}`,
            backgroundColor: colors.background.primary,
            '& .MuiToggleButton-root': {
              border: 'none',
              borderRadius: '6px !important',
              color: colors.brand.textSecondary,
              padding: '3px 12px',
              fontFamily: monoFontFamily,
              fontSize: '11.5px',
              textTransform: 'none',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)', color: colors.primary.white },
              '&.Mui-selected': {
                backgroundColor: 'rgba(255, 160, 21, 0.14)',
                color: colors.primary.yellow,
                '&:hover': { backgroundColor: 'rgba(255, 160, 21, 0.2)' },
              },
            },
          }}
        >
          <ToggleButton value="schema">Formulário</ToggleButton>
          <ToggleButton value="raw">JSON</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Box
        sx={{
          border: `1px solid ${colors.brand.line}`,
          borderRadius: '12px',
          padding: '16px',
          backgroundColor: colors.background.primary,
          minHeight: '250px',
          maxHeight: 'min(440px, 52vh)',
          overflowY: 'auto',
        }}
      >
        {viewMode === 'schema' ? (
          <JsonSchemaFormEditor schema={schema} formData={formData || {}} onChange={handleFormChange} />
        ) : (
          <RawJsonEditor value={jsonValue} onChange={handleJsonChange} errors={jsonErrors} />
        )}
      </Box>

      {jsonErrors.length > 0 && (
        <Box sx={{ ...noticeSX(colors.status.rejected), mt: 1.5 }}>
          <ErrorOutlineRoundedIcon />
          {jsonErrors.join(', ')}
        </Box>
      )}
    </Box>
  );
};
